'use client'

import React from 'react'

import { mergeRefs } from '../../utils/mergeRefs'
import {
  ButtonContent,
  Container,
  IconWrapper,
  InputContent,
  TextareaContent,
} from './styles'

type Variant = 'input' | 'textarea' | 'button'

interface BaseInputProps<T extends Variant = 'input'> {
  /**
   * Input variant
   */
  variant: T

  /**
   * Open state
   */
  open?: boolean

  /**
   * Error state
   */
  error?: boolean

  /**
   * Disabled input
   */
  disabled?: boolean

  /**
   * Set width to auto
   */
  autoWidth?: boolean
}

export interface InputVariantProps<T extends 'input' = 'input'>
  extends BaseInputProps<T> {
  /**
   * Variant
   */
  variant: T

  /**
   * id attribute
   */
  id?: string

  /**
   * Input type
   */
  type?: React.HTMLInputTypeAttribute

  /**
   * Value
   */
  value?: number | string

  /**
   * Placeholder
   */
  placeholder?: number | string

  /**
   * Icon before input
   */
  iconBefore?: React.ReactNode

  /**
   * Icon after input
   */
  iconAfter?: React.ReactNode

  /**
   * autoComplete attribute
   */
  autoComplete?: string

  /**
   * spellCheck attribute
   */
  spellCheck?: React.HTMLAttributes<HTMLInputElement>['spellCheck']

  /**
   * onChange handler
   */
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void

  /**
   * onFocus handler
   */
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void

  /**
   * onBlur handler
   */
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void

  /**
   * Required attribute
   */
  required?: boolean
}

export interface TextareaVariantProps<T extends 'textarea' = 'textarea'>
  extends BaseInputProps<T> {
  /**
   * Variant
   */
  variant: T

  /**
   * Value
   */
  value?: number | string

  /**
   * Placeholder
   */
  placeholder?: number | string

  /**
   * Auto adjust height
   */
  autoHeight?: boolean

  /**
   * onChange handler
   */
  onChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void

  /**
   * onFocus handler
   */
  onFocus?: (e: React.FocusEvent<HTMLTextAreaElement>) => void

  /**
   * onBlur handler
   */
  onBlur?: (e: React.FocusEvent<HTMLTextAreaElement>) => void
}

export interface ButtonVariantProps<T extends 'button' = 'button'>
  extends BaseInputProps<T> {
  /**
   * Variant
   */
  variant: T

  /**
   * Value
   */
  value?: React.ReactNode

  /**
   * Placeholder
   */
  placeholder?: React.ReactNode

  /**
   * Icon before input
   */
  iconBefore?: React.ReactNode

  /**
   * Icon after input
   */
  iconAfter?: React.ReactNode

  /**
   * onClick handler
   */
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void

  /**
   * onFocus handler
   */
  onFocus?: (e: React.FocusEvent<HTMLButtonElement>) => void

  /**
   * onBlur handler
   */
  onBlur?: (e: React.FocusEvent<HTMLButtonElement>) => void
}

type InputComponentElement<T extends Variant> = T extends 'input'
  ? HTMLInputElement
  : T extends 'textarea'
  ? HTMLTextAreaElement
  : T extends 'button'
  ? HTMLButtonElement
  : never

export type InputProps<T extends Variant = Variant> = T extends 'input'
  ? InputVariantProps<T>
  : T extends 'textarea'
  ? TextareaVariantProps<T>
  : T extends 'button'
  ? ButtonVariantProps<T>
  : never

const inputTypesWithNativeRightIcon: React.HTMLInputTypeAttribute[] = [
  'date',
  'datetime-local',
  'month',
  'time',
  'week',
  'number',
]

export const InputInner = <T extends Variant>(
  props: InputProps<T>,
  ref: React.ForwardedRef<InputComponentElement<T>>,
) => {
  const isButton = props.variant === 'button'
  const isInput = props.variant === 'input'
  const isTextarea = props.variant === 'textarea'

  const textareaRef = React.useRef<HTMLTextAreaElement>(null)

  const {
    value,
    placeholder,
    open = false,
    error = false,
    disabled = false,
    autoWidth = false,
    ...rest
  } = props

  // Adjust textarea height
  React.useEffect(() => {
    if (!isTextarea) return
    if (!textareaRef.current) return
    if (!props.autoHeight) return

    textareaRef.current.style.height = '0px'
    const scrollHeight = textareaRef.current.scrollHeight
    // Offset to prevent scrollbar appearing
    const OFFSET = 2
    textareaRef.current.style.height = `${scrollHeight + OFFSET}px`
  }, [
    isTextarea,
    props,
    // Watch value change so the height gets adjusted when the value changes
    props.value,
  ])

  return (
    <Container
      as={isButton ? 'button' : 'div'}
      ref={isButton ? (ref as React.Ref<HTMLButtonElement>) : undefined}
      type={isButton ? 'button' : undefined}
      isOpen={open}
      hasError={error}
      disabled={disabled}
      autoWidth={autoWidth}
      bordered={isTextarea}
      {...(isButton ? rest : {})}
      onClick={isButton ? props.onClick : undefined}
      onFocus={isButton ? props.onFocus : undefined}
      onBlur={isButton ? props.onBlur : undefined}
    >
      {!isTextarea && props.iconBefore && (
        <IconWrapper position="left">{props.iconBefore}</IconWrapper>
      )}

      {isButton && (
        <ButtonContent isPlaceholder={!value} disabled={!!disabled}>
          {value ?? placeholder}
        </ButtonContent>
      )}

      {isTextarea && (
        <TextareaContent
          ref={mergeRefs(textareaRef, ref as React.Ref<HTMLTextAreaElement>)}
          value={props.value}
          placeholder={placeholder ? `${placeholder}` : undefined}
          {...rest}
          onChange={isTextarea ? props.onChange : undefined}
          onFocus={isTextarea ? props.onFocus : undefined}
          onBlur={isTextarea ? props.onBlur : undefined}
          disabled={disabled}
        />
      )}

      {isInput && (
        <InputContent
          ref={ref as React.Ref<HTMLInputElement>}
          value={props.value}
          placeholder={placeholder ? `${placeholder}` : undefined}
          {...rest}
          type={props.type ?? 'text'}
          hasNativeRightIcon={inputTypesWithNativeRightIcon.includes(
            props.type ?? 'text',
          )}
          onChange={isInput ? props.onChange : undefined}
          onFocus={isInput ? props.onFocus : undefined}
          onBlur={isInput ? props.onBlur : undefined}
          disabled={disabled}
        />
      )}

      {!isTextarea && props.iconAfter && (
        <IconWrapper position="right">{props.iconAfter}</IconWrapper>
      )}
    </Container>
  )
}

export const Input = React.forwardRef(InputInner) as <T extends Variant>(
  props: InputProps<T> & {
    ref?: React.ForwardedRef<InputComponentElement<T>>
  },
) => ReturnType<typeof InputInner>
